/**
 * upgrade-verify.ts — what `ss upgrade` runs AFTER it has written: the
 * project-wide regeneration and the post-upgrade verification.
 *
 * - REGEN (default; `--no-regen` opts out): the frontend primitives
 *   (`scaffold-ui-primitives`) then the routes aggregation
 *   (`aggregate-component-registry`) are re-run with the scaffolders of THIS
 *   CLI, so pages re-scaffolded later resolve the primitives they import and
 *   the component registry carries the built-in pages of the new package.
 *   Each child CLI is first asked for its file list (`--dry-run`), the files
 *   are captured in the upgrade backup, THEN it writes.
 * - VERIFY (default; `--skip-verify` opts out): `dotnet build` + `dotnet test`
 *   of the solution, `npm run build` + `npm test` of the web app when it has
 *   those scripts. A red step marks the upgrade `partial` — the upgrade never
 *   restores on its own, it suggests `ss upgrade --restore latest`.
 *
 * Every process is started through an injected runner, so both phases are
 * testable without dotnet / npm.
 */
import fs from 'fs-extra';
import { tmpdir } from 'os';
import { isAbsolute, join } from 'path';
import { execSync } from 'child_process';
import type { BackupSet } from './upgrade-backup.js';

export interface CommandOutcome {
  ok: boolean;
  output: string;
}

export type CommandRunner = (command: string, cwd: string) => CommandOutcome;

/** Default runner: captured output, never throws. */
export const defaultRunner: CommandRunner = (command, cwd) => {
  try {
    const output = execSync(command, {
      cwd,
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'pipe'],
      maxBuffer: 64 * 1024 * 1024,
    });
    return { ok: true, output: output ?? '' };
  } catch (error) {
    const e = error as { stdout?: string | Buffer; stderr?: string | Buffer; message?: string };
    return { ok: false, output: `${e.stdout?.toString() ?? ''}${e.stderr?.toString() ?? ''}` || (e.message ?? '') };
  }
};

/** The last `n` non-empty lines — enough to see a compiler error, small enough for a JSON report. */
export function tail(output: string, n = 25): string {
  return output.split(/\r?\n/).filter((l) => l.trim() !== '').slice(-n).join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Verify
// ─────────────────────────────────────────────────────────────────────────────

export interface VerifyStep {
  name: string;
  command: string;
  cwd: string;
}

export interface VerifyStepResult extends VerifyStep {
  ok: boolean;
  outputTail?: string;
}

export interface VerifyResult {
  ok: boolean;
  steps: VerifyStepResult[];
  /** Why a whole side was not verified (no solution, no scripts…). */
  notes: string[];
}

/** The build/test steps of the project — the solution, then the web app. */
export async function planVerifySteps(projectDir: string, frontendDir: string | null): Promise<{ steps: VerifyStep[]; notes: string[] }> {
  const steps: VerifyStep[] = [];
  const notes: string[] = [];

  const solutions = (await fs.readdir(projectDir)).filter((f) => /\.slnx?$/i.test(f)).sort();
  // Prefer .sln (VS Code tooling) when both exist.
  const sln = solutions.find((f) => f.toLowerCase().endsWith('.sln')) ?? solutions[0];
  if (sln) {
    steps.push({ name: 'dotnet build', command: `dotnet build "${sln}" --nologo`, cwd: projectDir });
    steps.push({ name: 'dotnet test', command: `dotnet test "${sln}" --nologo --no-build`, cwd: projectDir });
  } else {
    notes.push('No .sln at the project root — the .NET side was not verified.');
  }

  if (frontendDir && (await fs.pathExists(join(frontendDir, 'package.json')))) {
    let scripts: Record<string, string> = {};
    try {
      scripts = (JSON.parse(await fs.readFile(join(frontendDir, 'package.json'), 'utf-8')) as { scripts?: Record<string, string> }).scripts ?? {};
    } catch {
      notes.push('web package.json unreadable — the web app was not verified.');
    }
    if (scripts.build) steps.push({ name: 'npm run build', command: 'npm run build', cwd: frontendDir });
    if (scripts.test) {
      // A freshly generated app may have no test file yet: that is not a regression.
      const passWithNoTests = /\b(vitest|jest)\b/.test(scripts.test) ? ' -- --passWithNoTests' : '';
      steps.push({ name: 'npm test', command: `npm test${passWithNoTests}`, cwd: frontendDir });
    }
    if (!scripts.build && !scripts.test) notes.push('The web app has no build/test script — not verified.');
  }
  return { steps, notes };
}

/** Run the steps in order; a failed build stops its own side (no `test --no-build` on a broken build). */
export function runVerify(steps: VerifyStep[], run: CommandRunner = defaultRunner, onStep?: (step: VerifyStep) => void): VerifyResult {
  const results: VerifyStepResult[] = [];
  const brokenCwd = new Set<string>();
  for (const step of steps) {
    if (brokenCwd.has(step.cwd)) continue;
    onStep?.(step);
    const outcome = run(step.command, step.cwd);
    results.push({ ...step, ok: outcome.ok, outputTail: outcome.ok ? undefined : tail(outcome.output) });
    if (!outcome.ok && /build/.test(step.name)) brokenCwd.add(step.cwd);
  }
  return { ok: results.every((r) => r.ok), steps: results, notes: [] };
}

// ─────────────────────────────────────────────────────────────────────────────
// Project-wide regeneration
// ─────────────────────────────────────────────────────────────────────────────

export interface RegenStepResult {
  name: string;
  ok: boolean;
  /** Files the scaffolder reported (project-relative paths). */
  files: string[];
  message?: string;
}

interface Envelope {
  success?: boolean;
  errors?: string[];
  data?: { files?: string[] };
  filesCreated?: string[];
  filesModified?: string[];
}

/** Parse a CLI JSON envelope from stdout (tolerates a log line before the JSON). */
export function parseEnvelope(output: string): Envelope | null {
  const trimmed = output.trim();
  for (const candidate of [trimmed, trimmed.slice(trimmed.indexOf('{'), trimmed.lastIndexOf('}') + 1)]) {
    try {
      return JSON.parse(candidate) as Envelope;
    } catch {
      /* next */
    }
  }
  return null;
}

export interface RegenInput {
  projectDir: string;
  frontendDir: string;
  appCode: string;
  /** `templates/skills` of the CLI package. */
  skillsDir: string;
  /** Where `npx tsx` resolves from (the CLI package root). */
  toolCwd: string;
  backup: BackupSet;
  run?: CommandRunner;
}

const q = (s: string) => `"${s}"`;

/**
 * Re-run the project-wide frontend scaffolders. Fail-soft per step: a refusal
 * (e.g. the aggregator's phantom-import guard) is reported, never thrown.
 */
export async function runProjectRegen(input: RegenInput): Promise<RegenStepResult[]> {
  const run = input.run ?? defaultRunner;
  const results: RegenStepResult[] = [];
  const toProjectRel = (p: string) => input.backup.relOf(isAbsolute(p) ? p : join(input.frontendDir, p));
  const specFile = join(tmpdir(), `ss-upgrade-ui-primitives-${process.pid}.json`);

  const steps: Array<{ name: string; cli: string; args: () => Promise<string>; dryFlag: string }> = [
    {
      name: 'scaffold-ui-primitives',
      cli: join(input.skillsDir, 'development', 'frontend', 'ui-primitives', 'cli', 'scaffold-ui-primitives', 'index.ts'),
      args: async () => {
        await fs.writeFile(specFile, JSON.stringify({ projectPath: input.frontendDir, appCode: input.appCode }), 'utf-8');
        return `--spec-file ${q(specFile)}`;
      },
      dryFlag: '--dry_run',
    },
    {
      name: 'aggregate-component-registry',
      cli: join(input.skillsDir, 'development', 'frontend', 'routes', 'cli', 'aggregate-component-registry', 'index.ts'),
      args: async () => `--project-path ${q(input.frontendDir)}`,
      dryFlag: '--dry-run',
    },
  ];

  for (const step of steps) {
    if (!(await fs.pathExists(step.cli))) {
      results.push({ name: step.name, ok: false, files: [], message: 'scaffolder not found in the CLI package — skipped' });
      continue;
    }
    const args = await step.args();
    const base = `npx --prefer-offline tsx ${q(step.cli)} ${args}`;

    const dry = parseEnvelope(run(`${base} ${step.dryFlag}`, input.toolCwd).output);
    if (!dry || dry.success === false) {
      results.push({ name: step.name, ok: false, files: [], message: dry?.errors?.join(' | ') || 'dry run failed — nothing written' });
      continue;
    }
    const planned = (dry.data?.files ?? []).map(toProjectRel);
    await input.backup.capture(planned);

    const real = run(base, input.toolCwd);
    const env = parseEnvelope(real.output);
    if (!real.ok || !env || env.success === false) {
      results.push({ name: step.name, ok: false, files: planned, message: env?.errors?.join(' | ') || tail(real.output, 5) || 'failed' });
      continue;
    }
    results.push({ name: step.name, ok: true, files: planned });
  }
  await fs.remove(specFile);
  return results;
}
